import React, { useEffect, useState } from 'react';
import { css, cx } from 'emotion';
import { visuallyHidden } from '@guardian/src-foundations/accessibility';
import { Pillars } from '@root/src/web/components/Pillars';
import { GuardianRoundel } from '@root/src/web/components/GuardianRoundel';
import { space } from '@guardian/src-foundations';
import { until } from '@guardian/src-foundations/mq';
import { ThemeProvider } from 'emotion-theming';
import { Button, buttonReaderRevenueBrand } from '@guardian/src-button';
import { SvgArrowRightStraight } from '@guardian/src-icons';
import { Hide } from '@frontend/web/components/Hide';
import { clearFix } from '@root/src/lib/mixins';
import { Display } from '@guardian/types';
import { buildID, navInputCheckboxId, showMoreButtonId, veggieBurgerId, } from '@root/src/web/components/Nav/config';
import { ExpandedMenu } from './ExpandedMenu/ExpandedMenu';
const clearFixStyle = css `
	${clearFix};
`;
const rowStyles = css `
	display: flex;
	flex-direction: row;
	justify-content: space-between;
`;
const minHeight = css `
	min-height: 48px;
`;
const PositionRoundel = ({ children }) => (React.createElement("div", { className: css `
			margin-top: 3px;
			z-index: 2;

			${until.desktop} {
				margin-right: 51px;
			}

			margin-right: 24px;
		` }, children));
const PositionButton = ({ children }) => (React.createElement("div", { className: css `
			margin-top: ${space[1]}px;
			margin-left: ${space[2]}px;
		` }, children));
// Will lazily fetch required data for expanded menu when menu is expanded by user.
export const Nav = ({ format, subscribeUrl, edition, topLevelPillars, currentNavLinkTitle, ID, ajaxUrl, }) => {
    const [dataRequired, setDataRequired] = useState(false);
    // Accessibility
    useEffect(() => {
        // Used to toggle data-link-name on label buttons
        const navInputCheckbox = document.getElementById(`${buildID(ID, navInputCheckboxId)}`);
        const showMoreButton = document.getElementById(`${buildID(ID, showMoreButtonId)}`);
        const veggieBurger = document.getElementById(`${buildID(ID, veggieBurgerId)}`);
        const expandedMenuClickableTags = document.querySelectorAll('.selectableMenuItem');
        const expandedMenu = document.getElementById(`${buildID(ID, 'expanded-menu')}`);
        if (navInputCheckbox === null)
            return;
        navInputCheckbox.addEventListener('click', () => {
            if (!navInputCheckbox.checked) {
                showMoreButton.setAttribute('data-link-name', 'nav2 : veggie-burger: show');
                veggieBurger.setAttribute('data-link-name', 'nav2 : veggie-burger: show');
                expandedMenuClickableTags.forEach(($selectableElement) => {
                    $selectableElement.setAttribute('tabindex', '-1');
                });
            }
            else {
                showMoreButton.setAttribute('data-link-name', 'nav2 : veggie-burger: hide');
                veggieBurger.setAttribute('data-link-name', 'nav2 : veggie-burger: hide');
                expandedMenuClickableTags.forEach(($selectableElement) => {
                    $selectableElement.setAttribute('tabindex', '0');
                });
                // focusOnFirstNavElement(); TODO but tricky with current approach
            }
        });
        const toggleMainMenu = () => {
            navInputCheckbox?.click();
        };
        // Close hide menu on press enter
        const keydownToggleMainMenu = (e) => {
            // keyCode: 13 => Enter key | keyCode: 32 => Space key
            if (e.keyCode === 13 || e.keyCode === 32) {
                e.preventDefault();
                toggleMainMenu();
            }
        };
        showMoreButton?.addEventListener('keydown', keydownToggleMainMenu);
        veggieBurger?.addEventListener('keydown', keydownToggleMainMenu);
        // Accessibility to hide Nav when pressing escape key
        document.addEventListener('keydown', (e) => {
            // keyCode: 27 => esc
            if (e.keyCode === 27) {
                if (navInputCheckbox?.checked) {
                    toggleMainMenu();
                    if (window.getComputedStyle(veggieBurger).display === 'none') {
                        showMoreButton.focus();
                    }
                    else {
                        veggieBurger.focus();
                    }
                }
            }
        });
        // onBlur close dialog
        document.addEventListener('mousedown', (e) => {
            if (navInputCheckbox.checked &&
                !expandedMenu.contains(e.target)) {
                toggleMainMenu();
            }
        });
    }, [ID]);
    return (React.createElement("div", { className: rowStyles },
        React.createElement("nav", { className: cx(clearFixStyle, rowStyles, format.display === Display.Immersive && minHeight), role: "navigation", "aria-label": "Guardian sections", "data-component": "nav2" },
            format.display === Display.Immersive && (React.createElement(Hide, { when: "above", breakpoint: "tablet" },
                React.createElement(ThemeProvider, { theme: buttonReaderRevenueBrand },
                    React.createElement(PositionButton, null,
                        React.createElement(Button, { priority: "primary", size: "small", iconSide: "right", icon: React.createElement(SvgArrowRightStraight, null), "data-link-name": "nav2 : support-cta", "data-edition": edition, onClick: () => {
                                window.location.href = subscribeUrl;
                                return false;
                            } }, "Subscribe"))))),
            React.createElement("input", { type: "checkbox", className: css `
						${visuallyHidden};
					`, id: buildID(ID, navInputCheckboxId), name: "more", tabIndex: -1, key: "OpenExpandedMenuCheckbox", "aria-hidden": "true", onClick: () => setDataRequired(true) }),
            React.createElement(Pillars, { display: format.display, pillars: topLevelPillars, pillar: format.theme, dataLinkName: "nav2", isTopNav: true }),
            React.createElement(ExpandedMenu, { display: format.display, ID: ID, expand: dataRequired, currentNavLinkTitle: currentNavLinkTitle, edition: edition, ajaxUrl: ajaxUrl })),
        format.display === Display.Immersive && (React.createElement(PositionRoundel, null,
            React.createElement(GuardianRoundel, null)))));
};
